import React, { useState, useEffect } from 'react';
import {
  ITEM_SNOOZE_PRESETS,
  MAX_SNOOZE_HELPER_TEXT,
  maxSnoozeDate,
} from '../ItemAvailability/itemSnoozePresets';
import { VariantScopeList } from '../ItemAvailability/VariantScopeList';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Fab,
  Button,
  Stack,
  Autocomplete,
  createFilterOptions,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { BottomSheet, BottomSheetActions } from './BottomSheet';
import { HelpTooltip } from '../Onboarding';
import { useTimeFormat } from '../../context/TimeFormatContext';
import { formatSnoozeDateTime, PICKER_DATE_TIME_FORMAT } from '../../utils/formatTime';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { MobileStorePicker, type MobileStore } from './MobileStorePicker';
import { useStoreManagement } from '../../context/StoreManagementContext';
import { useStoresQuery } from '../../hooks/queries';
import {
  getStoreItemAvailability,
  createItemBlackout,
  deleteItemBlackout,
  bulkDeleteItemBlackouts,
} from '../../api';
import { useProductVariantScope } from '../../hooks/useProductVariantScope';
import { useSnoozeItemOptions } from '../../hooks/useSnoozeItemOptions';
import {
  BlackoutItem,
  BlackoutType,
  BlackoutReason,
  BlackoutTypeLabels,
  BlackoutReasonLabels,
  getBlackoutType,
  getItemCode,
  getItemDisplayName,
} from '../ItemAvailability/types';
import type { ScopedSelectedItem as SelectedItem } from '../../hooks/useProductVariantScope';

export const MobileSnoozeItems: React.FC = () => {
  const { use24HourFormat } = useTimeFormat();
  const { data: stores = [], isLoading: storesLoading } = useStoresQuery();
  const { selectedMobileStore, setSelectedMobileStore } = useStoreManagement();
  const selectedStoreNumber = selectedMobileStore;
  const setSelectedStoreNumber = setSelectedMobileStore;
  const [blackouts, setBlackouts] = useState<BlackoutItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBlackout, setSelectedBlackout] = useState<BlackoutItem | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [itemType, setItemType] = useState<BlackoutType>('PRODUCT');
  const [reason, setReason] = useState<BlackoutReason>('OUT_OF_STOCK');
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  // No default duration. The confirm button used to sit in a pinned footer, so
  // users tapped Snooze before scrolling to the duration and got an accidental
  // end-of-day snooze. Submit stays disabled until a duration is chosen.
  const [expiry, setExpiry] = useState<Date | null>(null);
  const [selectedQuickOption, setSelectedQuickOption] = useState<string>('');

  // Multi-select activate on the snoozed list. Rows keyed `${type}:${code}`,
  // matching the desktop item page.
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [bulkActivateOpen, setBulkActivateOpen] = useState(false);

  // Variant drill-down — matches the desktop item page. Every selected product
  // keeps its own scope, so adding a second item no longer discards the first
  // item's variant picks.
  const variantScope = useProductVariantScope({
    isProductType: itemType === 'PRODUCT',
    selectedItems,
  });
  const { resetVariantState, hasInvalidVariantScope, buildSubmitPayload, getSnoozeUnitCount } =
    variantScope;
  const snoozeUnitCount = getSnoozeUnitCount(selectedItems);
  const snoozeButtonLabel =
    snoozeUnitCount > 0
      ? `Snooze ${snoozeUnitCount} item${snoozeUnitCount === 1 ? '' : 's'}`
      : 'Snooze';

  // Item options: live published menus when loaded (browsable, snoozed items
  // excluded), catalog search as fallback / behind the toggle.
  const {
    mode: optionsMode,
    options: itemOptions,
    visibleMenuOptions,
    inputValue: searchQuery,
    onInputChange: setSearchQuery,
    isLoading: isSearching,
    menuStatus,
    useCatalogSearch,
    setUseCatalogSearch,
    reset: resetItemOptions,
  } = useSnoozeItemOptions({
    storeNumber: selectedStoreNumber,
    itemType,
    blackouts,
  });

  // Filter state
  const [filterQuery, setFilterQuery] = useState('');

  // Add every currently-shown live-menu option to the selection, matching the
  // desktop item page.
  const handleSelectAllShown = () => {
    setSelectedItems(prev => {
      const byCode = new Map(prev.map(i => [i.code, i]));
      for (const opt of visibleMenuOptions) {
        if (!byCode.has(opt.code)) {
          byCode.set(opt.code, { type: itemType, code: opt.code, name: opt.name });
        }
      }
      return [...byCode.values()];
    });
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      resetForm();
    }
  }, [dialogOpen]);

  // Convert stores to mobile format
  const mobileStores: MobileStore[] = stores.map(s => ({
    storeNumber: s.storeNumber,
    name: s.name || s.storeNumber,
    isActive: s.isActive,
  }));

  // Auto-select first store if only one
  useEffect(() => {
    if (mobileStores.length === 1 && !selectedStoreNumber) {
      setSelectedStoreNumber(mobileStores[0].storeNumber);
    }
  }, [mobileStores, selectedStoreNumber]);

  // Fetch blackouts when store changes
  useEffect(() => {
    if (selectedStoreNumber) {
      fetchBlackouts();
    }
  }, [selectedStoreNumber]);

  const fetchBlackouts = async () => {
    if (!selectedStoreNumber) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getStoreItemAvailability(selectedStoreNumber, 100);
      setBlackouts(response?.blackouts || []);
    } catch (err) {
      console.error('Failed to fetch snoozed items:', err);
      setError('Failed to load snoozed items');
      setBlackouts([]);
    } finally {
      setLoading(false);
    }
  };


  const handleCreateSnooze = async () => {
    if (!selectedStoreNumber || selectedItems.length === 0 || !expiry) return;
    if (hasInvalidVariantScope) return;
    if (expiry > maxSnoozeDate()) {
      setError(MAX_SNOOZE_HELPER_TEXT);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Applies each product's variant scope. Products narrowed to specific
      // variants send only variant codes — the parent is left available so
      // sibling variants stay orderable. `auditItems` carries display names
      // for the activity log.
      const {
        productCodes,
        bundleCodes,
        variantCodes,
        modifierCodes,
        optionValueCodes,
        auditItems,
      } = buildSubmitPayload(selectedItems);

      await createItemBlackout(
        [selectedStoreNumber],
        productCodes,
        bundleCodes,
        reason,
        expiry.toISOString(),
        new Date().toISOString(),
        variantCodes,
        modifierCodes,
        optionValueCodes,
        auditItems
      );

      const snoozedCount = snoozeUnitCount;

      setDialogOpen(false);
      resetForm();
      setSuccess(`${snoozedCount} item${snoozedCount === 1 ? '' : 's'} snoozed successfully`);
      setTimeout(() => setSuccess(null), 3000);
      fetchBlackouts();
    } catch (err) {
      console.error('Failed to snooze item:', err);
      setError('Failed to snooze item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnsnooze = async () => {
    if (!selectedStoreNumber || !selectedBlackout) return;

    setSubmitting(true);
    setError(null);

    try {
      const type = getBlackoutType(selectedBlackout);
      const code = getItemCode(selectedBlackout);

      await deleteItemBlackout(selectedStoreNumber, type, code, selectedBlackout.name);

      setDeleteDialogOpen(false);
      setSelectedBlackout(null);
      setSuccess('Item activated');
      setTimeout(() => setSuccess(null), 3000);
      fetchBlackouts();
    } catch (err: any) {
      console.error('Failed to activate item:', err);
      setError(err.response?.data?.message || 'Failed to activate item');
    } finally {
      setSubmitting(false);
    }
  };

  // Clear selection whenever the list reloads so stale keys can't linger.
  useEffect(() => {
    setSelectedRows(new Set());
  }, [blackouts]);

  const rowKey = (b: BlackoutItem) => `${getBlackoutType(b)}:${getItemCode(b)}`;

  const toggleRow = (blackout: BlackoutItem) => {
    const key = rowKey(blackout);
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleBulkActivate = async () => {
    if (!selectedStoreNumber) return;
    const selected = blackouts.filter(b => selectedRows.has(rowKey(b)));
    if (selected.length === 0) return;

    setBulkActivateOpen(false);
    setSubmitting(true);
    setError(null);

    try {
      // Group selected codes by type for the bulk-delete endpoint, carrying
      // names for the Activity Log. Mirrors the desktop item page.
      const codesByType: Record<string, string[]> = {
        PRODUCT: [], BUNDLE: [], VARIANT: [], MODIFIER: [], OPTION_VALUE: [],
      };
      const items = selected.map(b => {
        const type = getBlackoutType(b);
        const code = getItemCode(b);
        codesByType[type].push(code);
        return { type, code, name: b.name || code };
      });

      const response = await bulkDeleteItemBlackouts({
        storeNumbers: [selectedStoreNumber],
        productCodes: codesByType.PRODUCT,
        bundleCodes: codesByType.BUNDLE,
        variantCodes: codesByType.VARIANT,
        modifierCodes: codesByType.MODIFIER,
        optionValueCodes: codesByType.OPTION_VALUE,
        items,
      });

      const failed = response?.summary?.failed ?? 0;
      if (response?.status && failed === 0) {
        setSuccess(`Activated ${selected.length} item${selected.length === 1 ? '' : 's'}`);
        setTimeout(() => setSuccess(null), 3000);
        setSelectedRows(new Set());
      } else {
        setError(`Activated ${response?.summary?.succeeded ?? 0}, failed ${failed}. Please retry.`);
      }
      await fetchBlackouts();
    } catch (err: any) {
      console.error('Failed to bulk activate items:', err);
      setError(err.response?.data?.message || err.message || 'Failed to activate items');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setItemType('PRODUCT');
    setReason('OUT_OF_STOCK');
    resetItemOptions();
    setSelectedItems([]);
    setExpiry(null);
    setSelectedQuickOption('');
    resetVariantState();
  };

  const getTimeRemaining = (expiryDate: string) => {
    const now = new Date();
    const exp = new Date(expiryDate);
    const diff = exp.getTime() - now.getTime();

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d remaining`;
    }

    return `${hours}h ${minutes}m`;
  };

  const filteredBlackouts = blackouts.filter(b => {
    if (!filterQuery) return true;
    const searchTerm = filterQuery.toLowerCase();
    const code = getItemCode(b).toLowerCase();
    const name = (b.name || '').toLowerCase();
    return code.includes(searchTerm) || name.includes(searchTerm);
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
      {/* Store Picker */}
      <MobileStorePicker
        stores={mobileStores}
        selectedStore={selectedStoreNumber}
        onStoreChange={setSelectedStoreNumber}
        loading={storesLoading}
      />

      {/* Search/Filter */}
      {selectedStoreNumber && (
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8f9fa' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Filter snoozed items..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.light',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mx: 2, mt: 1, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 4 }}>
        {!selectedStoreNumber ? (
          // Empty state - welcome banner in store picker handles this
          null
        ) : loading || storesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredBlackouts.length === 0 ? (
          <Box 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            <Typography color="text.secondary">
              {filterQuery ? 'No matching items' : 'No items are currently snoozed'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Tap + to snooze an item
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            {/* Header with count */}
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                  }}
                >
                  Snoozed Items
                </Typography>
                <HelpTooltip title="Items temporarily removed from the menu. Tap the checkmark to activate an item, or tick several and activate them together." iconSize={14} />
              </Box>
              <Chip
                size="small"
                label={filteredBlackouts.length}
                sx={{ 
                  height: 18, 
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: 'warning.50',
                  color: 'warning.dark',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Box>

            {/* Bulk activate bar — matches the desktop item page. */}
            {selectedRows.size > 0 && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'primary.50',
                }}
              >
                <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
                  {selectedRows.size} selected
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  disabled={submitting}
                  onClick={() => setBulkActivateOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Activate ({selectedRows.size})
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  disabled={submitting}
                  onClick={() => setSelectedRows(new Set())}
                  sx={{ textTransform: 'none' }}
                >
                  Clear
                </Button>
              </Box>
            )}
            <List disablePadding dense>
              {filteredBlackouts.map((blackout, index) => {
                const type = getBlackoutType(blackout);
                const isExpired = new Date(blackout.expiry) < new Date();
                const isRowSelected = selectedRows.has(rowKey(blackout));

                return (
                  <React.Fragment key={index}>
                    {/* Everything sits in normal flex flow. ListItemSecondaryAction
                        positions absolutely and relies on MUI's 48px right
                        padding, which any `pr` override here silently removes —
                        that put the activate tick on top of the type chip. */}
                    <ListItem
                      selected={isRowSelected}
                      sx={{
                        py: 1,
                        pl: 0.5,
                        pr: 1,
                        gap: 0.5,
                        minHeight: 56,
                      }}
                    >
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={isRowSelected}
                        onChange={() => toggleRow(blackout)}
                        sx={{ mr: 0.5 }}
                      />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1, minWidth: 0 }}>
                              {getItemDisplayName(blackout)}
                            </Typography>
                            <Chip
                              label={BlackoutTypeLabels[type]}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                            <Chip
                              label={BlackoutReasonLabels[blackout.reason]}
                              size="small"
                              color={blackout.reason === 'OUT_OF_STOCK' ? 'error' : 'warning'}
                              sx={{ height: 16, fontSize: '0.55rem' }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                              <TimeIcon sx={{ fontSize: 12, color: isExpired ? 'error.main' : 'text.disabled' }} />
                              <Typography variant="caption" color={isExpired ? 'error.main' : 'text.secondary'} sx={{ fontSize: '0.65rem' }}>
                                {getTimeRemaining(blackout.expiry)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      <IconButton
                        size="small"
                        aria-label={`Activate ${getItemDisplayName(blackout)}`}
                        onClick={() => {
                          setSelectedBlackout(blackout);
                          setDeleteDialogOpen(true);
                        }}
                        sx={{ color: 'success.main', flexShrink: 0 }}
                      >
                        <CheckIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </ListItem>
                    {index < filteredBlackouts.length - 1 && <Divider sx={{ ml: 6, mr: 6 }} />}
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        )}
        
        {/* Bottom spacer */}
        <Box sx={{ height: 80 }} />
      </Box>

      {/* FAB to add */}
      {selectedStoreNumber && (
        <Fab
          color="primary"
          onClick={() => setDialogOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 32px)',
            right: 16,
            boxShadow: '0 4px 12px rgba(102, 44, 139, 0.3)',
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Snooze Item Dialog */}
      <BottomSheet
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); resetForm(); }}
        title="Snooze Item"
        subtitle="Temporarily disable items from ordering"
        maxHeight="90vh"
        // Unpinned: users were tapping a pinned Snooze button before scrolling
        // to the duration, snoozing to end-of-day by accident.
        inlineActions
        actions={
          <BottomSheetActions
            onCancel={() => { setDialogOpen(false); resetForm(); }}
            onConfirm={handleCreateSnooze}
            confirmText={snoozeButtonLabel}
            confirmDisabled={
              submitting ||
              selectedItems.length === 0 ||
              !expiry ||
              hasInvalidVariantScope
            }
            confirmLoading={submitting}
          />
        }
      >
        <Stack spacing={2}>
          {/* Item Type Chips */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
              ITEM TYPE
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {[
                { value: 'PRODUCT', label: 'Product' },
                { value: 'BUNDLE', label: 'Bundle' },
                { value: 'MODIFIER', label: 'Modifier' },
                // VARIANT and OPTION_VALUE excluded from creation here — see
                // SELECTABLE_BLACKOUT_TYPES in ItemAvailability/types.ts.
              ].map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  variant={itemType === opt.value ? 'filled' : 'outlined'}
                  color={itemType === opt.value ? 'primary' : 'default'}
                  onClick={() => {
                    setItemType(opt.value as BlackoutType);
                    resetItemOptions();
                    setSelectedItems([]);
                  }}
                  sx={{ fontWeight: itemType === opt.value ? 600 : 400 }}
                />
              ))}
            </Stack>
          </Box>

          {/* Search / browse the live menu */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                SEARCH & SELECT ITEMS
              </Typography>
              {(menuStatus === 'ready' || menuStatus === 'partial') && (
                <Chip
                  size="small"
                  label={optionsMode === 'menu' ? 'Live menu' : 'Full catalog'}
                  color={optionsMode === 'menu' ? 'primary' : 'default'}
                  variant="outlined"
                  onClick={() => {
                    setUseCatalogSearch(!useCatalogSearch);
                    resetItemOptions();
                  }}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Box>
            {/* Select-all affordance for the live-menu list, matching desktop.
                Acts on the currently-shown (text-filtered) options. */}
            {optionsMode === 'menu' && visibleMenuOptions.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button size="small" onClick={handleSelectAllShown} sx={{ textTransform: 'none' }}>
                  {searchQuery.trim()
                    ? `Select all shown (${visibleMenuOptions.length})`
                    : `Select all (${visibleMenuOptions.length})`}
                </Button>
                {selectedItems.length > 0 && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => setSelectedItems([])}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear ({selectedItems.length})
                  </Button>
                )}
              </Stack>
            )}
            <Autocomplete
              multiple
              disableCloseOnSelect
              value={selectedItems.map(item => ({
                code: item.code,
                name: item.name,
                label: `${item.code} - ${item.name}`,
              }))}
              onChange={(_, newValue) => {
                setSelectedItems(
                  newValue.map(item => ({
                    type: itemType,
                    code: item.code,
                    name: item.name,
                  }))
                );
              }}
              inputValue={searchQuery}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  setSearchQuery(value);
                }
              }}
              options={itemOptions}
              filterOptions={
                optionsMode === 'menu'
                  ? createFilterOptions({ stringify: (opt) => `${opt.code} ${opt.name}` })
                  : (x) => x
              }
              getOptionLabel={(opt) => opt.label || `${opt.code} - ${opt.name}`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              loading={isSearching}
              loadingText="Searching..."
              noOptionsText={
                optionsMode === 'menu'
                  ? 'No matching items on this store\'s live menu'
                  : searchQuery.length < 1
                  ? 'Type to search...'
                  : 'No results'
              }
              size="small"
              // Selections are summarised in the panel below, so keep the input
              // itself compact — chips inside it would eat the sheet on a phone.
              renderTags={() => null}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props as any;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.code}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={optionsMode === 'menu' ? 'Browse or filter the live menu…' : 'Enter code or name...'}
                  InputProps={{
                    ...params.InputProps,
                    sx: { borderRadius: 2 },
                    endAdornment: (
                      <>
                        {isSearching && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Selected Items */}
          {selectedItems.length > 0 && (
            <Box sx={{ p: 1.5, bgcolor: 'primary.50', borderRadius: 2, border: 1, borderColor: 'primary.100' }}>
              <Typography variant="caption" color="primary.main" fontWeight={600}>
                {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                {selectedItems.map((item) => (
                  <Chip
                    key={item.code}
                    label={item.name}
                    size="small"
                    onDelete={() => setSelectedItems(selectedItems.filter(i => i.code !== item.code))}
                    sx={{ maxWidth: 150 }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Per-product variant drill-down. Each selected product keeps its
              own scope, so adding a second item no longer discards the first
              item’s variant picks. */}
          <VariantScopeList scope={variantScope} presentation="mobile" />

          {/* Duration — same preset set as desktop and the bulk editor. */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                DURATION
              </Typography>
              <HelpTooltip title="Item automatically returns to menu after this time. EOD = End of Day (5 AM)." iconSize={14} />
            </Box>
            {/* No duration is pre-selected, so say why Snooze is still disabled. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {expiry ? MAX_SNOOZE_HELPER_TEXT : `Choose how long to snooze for. ${MAX_SNOOZE_HELPER_TEXT}`}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
              {ITEM_SNOOZE_PRESETS.map((preset) => (
                <Chip
                  key={preset.key}
                  label={preset.shortLabel}
                  size="small"
                  variant={selectedQuickOption === preset.key ? 'filled' : 'outlined'}
                  color={selectedQuickOption === preset.key ? 'primary' : 'default'}
                  onClick={() => {
                    setExpiry(preset.getValue());
                    setSelectedQuickOption(preset.key);
                  }}
                />
              ))}
            </Stack>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDateTimePicker
                format={PICKER_DATE_TIME_FORMAT}
                label="Custom time"
                value={expiry}
                onChange={(val) => {
                  setExpiry(val);
                  setSelectedQuickOption('');
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                    helperText: expiry ? `Reactivates: ${formatSnoozeDateTime(expiry, use24HourFormat)}` : null,
                  },
                }}
                minDateTime={new Date()}
                // Byte Graph rejects expiries past the ceiling — grey them out.
                maxDateTime={maxSnoozeDate()}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
      </BottomSheet>

      {/* Activate Item Dialog */}
      <BottomSheet
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Activate Item"
        maxHeight="50vh"
        actions={
          <BottomSheetActions
            onCancel={() => setDeleteDialogOpen(false)}
            onConfirm={handleUnsnooze}
                        confirmText="Activate"
            confirmColor="success"
            confirmDisabled={submitting}
            confirmLoading={submitting}
            confirmIcon={<CheckIcon />}
          />
        }
      >
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'success.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <CheckIcon sx={{ color: 'success.main', fontSize: 28 }} />
          </Box>
          <Typography variant="body1" sx={{ mb: 1 }}>
            This item will become available for ordering immediately.
          </Typography>
          {selectedBlackout && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {getItemDisplayName(selectedBlackout)}
              </Typography>
            </Box>
          )}
        </Box>
      </BottomSheet>

      {/* Bulk Activate Confirmation */}
      <BottomSheet
        open={bulkActivateOpen}
        onClose={() => setBulkActivateOpen(false)}
        title={`Activate ${selectedRows.size} item${selectedRows.size === 1 ? '' : 's'}?`}
        maxHeight="60vh"
        actions={
          <BottomSheetActions
            onCancel={() => setBulkActivateOpen(false)}
            onConfirm={handleBulkActivate}
                        confirmText={`Activate ${selectedRows.size}`}
            confirmColor="success"
            confirmDisabled={submitting}
            confirmLoading={submitting}
            confirmIcon={<CheckIcon />}
          />
        }
      >
        <Typography variant="body2" color="text.secondary" gutterBottom>
          These items will be un-snoozed and immediately available again:
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, maxHeight: 220, overflow: 'auto' }}>
          {blackouts
            .filter(b => selectedRows.has(rowKey(b)))
            .map(b => (
              <li key={rowKey(b)}>
                <Typography variant="body2">{getItemDisplayName(b)}</Typography>
              </li>
            ))}
        </Box>
      </BottomSheet>
    </Box>
  );
};
